import { createId } from "../utils/crypto";
import { readMembership, type MembershipState } from "../membership";
import { apiPost } from "./http";

interface AuthPayload {
  token?: string;
  access_token?: string;
  refresh_token?: string;
  user?: unknown;
  is_anonymous?: boolean;
  membership?: unknown;
  member?: unknown;
  vip?: unknown;
  [key: string]: unknown;
}

interface AuthResponse extends AuthPayload {
  data?: AuthPayload;
}

export interface LoginResult {
  token: string;
  refreshToken?: string;
  user?: unknown;
  isAnonymous?: boolean;
  membership?: MembershipState;
}

export interface AnonymousRegisterInput {
  packageId: number;
  installId: string;
  deviceFingerprint: string;
  fingerprintComponents: Record<string, unknown>;
  inviteCode?: string;
}

export interface EmailRegisterInput {
  email: string;
  nickname: string;
  password: string;
  code: string;
  inviteCode?: string;
  token?: string;
  bindCurrentUser?: boolean;
}

function readNestedAnonymous(data: AuthResponse["data"] | AuthResponse) {
  const user = data?.user;
  if (user && typeof user === "object" && "is_anonymous" in user) {
    return Boolean((user as { is_anonymous?: unknown }).is_anonymous);
  }

  return Boolean(data && "is_anonymous" in data && data.is_anonymous);
}

export class AuthClient {
  private static readAuthResult(response: AuthResponse): LoginResult {
    const data = response.data ?? response;
    const token = data.token ?? data.access_token;

    if (!token) {
      throw new Error("登录接口未返回 token");
    }

    return {
      token,
      refreshToken: data.refresh_token,
      user: data.user ?? data,
      isAnonymous: readNestedAnonymous(data),
      membership: readMembership(data) ?? readMembership(data.user)
    };
  }

  static async login(account: string, password: string, inviteCode?: string): Promise<LoginResult> {
    const response = await apiPost<AuthResponse>("/v1/auth/login", {
      body: {
        account,
        password,
        invite_code: inviteCode || undefined
      }
    });

    return this.readAuthResult(response);
  }

  static async registerAnonymous(input: AnonymousRegisterInput): Promise<LoginResult> {
    const response = await apiPost<AuthResponse>("/v1/auth/anonymous", {
      body: {
        package_id: input.packageId,
        install_id: input.installId,
        device_fingerprint: input.deviceFingerprint,
        h5_fingerprint: input.deviceFingerprint,
        fingerprint_components: input.fingerprintComponents,
        client_nonce: createId(),
        invite_code: input.inviteCode || undefined
      }
    });

    return this.readAuthResult(response);
  }

  static async sendEmailCode(email: string) {
    try {
      await apiPost("/v1/auth/email-code", {
        body: {
          email,
          purpose: "register"
        }
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (!message.includes("404")) {
        throw error;
      }

      await apiPost("/v1/auth/send-email-code", {
        body: {
          email,
          scene: "register"
        }
      });
    }
  }

  static async registerEmail(input: EmailRegisterInput): Promise<LoginResult> {
    if (input.bindCurrentUser && !input.token) {
      throw new Error("绑定邮箱前需要先创建普通用户");
    }

    const response = await apiPost<AuthResponse>("/v1/auth/register", {
      token: input.token,
      body: {
        account: input.email,
        email: input.email,
        nickname: input.nickname,
        password: input.password,
        code: input.code,
        invite_code: input.inviteCode || undefined,
        bind_current_user: input.bindCurrentUser || undefined
      }
    });

    return this.readAuthResult(response);
  }
}
